import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext.jsx';
import { supabase } from '../supabase.js';
import { checkStaff, formatDateTime } from './moderation.js';
import './moderation.css';
import {
  labelFor, loadOutreachOverview, reasonLabels, setOutreachPaused, statusLabels, validatePauseReason,
} from '../outreach/outreach.js';
import { SignInGate } from '../SignIn.jsx';

const tabs = [['jobs', 'Jobs'], ['invites', 'Invitations'], ['candidates', 'Candidate log'], ['changes', 'Pause history']];

/** Staff-only supplier outreach log and pause switch. The database refuses non-staff. */
export function OutreachPanel() {
  const { user, loading } = useAuth();
  const [access, setAccess] = useState(null);
  useEffect(() => {
    let live = true;
    if (!user) { setAccess(null); return undefined; }
    checkStaff(supabase).then((value) => { if (live) setAccess(value); });
    return () => { live = false; };
  }, [user]);
  if (loading) return <div className="content-wrap">Loading account…</div>;
  if (!user) return <SignInGate next="/admin/outreach"/>;
  if (!access) return <div className="content-wrap">Checking access…</div>;
  if (!access.staff) return <div className="content-wrap account-page"><h1>You don't have access</h1><p>This area is for Resident Service 360 staff only.</p><Link to="/account">Back to your account</Link></div>;
  return <Panel />;
}

export default OutreachPanel;

function Panel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('jobs');
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((value) => value + 1), []);
  useEffect(() => {
    let live = true;
    loadOutreachOverview(supabase, 50).then((value) => { if (live) { setData(value); setError(''); } }).catch((cause) => { if (live) setError(cause.message); });
    return () => { live = false; };
  }, [version]);

  return <div className="content-wrap account-page">
    <header className="coord-page-heading">
      <div>
        <span className="account-kicker">SUPPLIER OUTREACH</span>
        <h1>Invitations to local businesses.</h1>
        <p>Every business we found, what we decided and why. Emails also need OUTREACH_ENABLED on the server.</p>
      </div>
    </header>
    {error && <p className="mod-error" role="alert">{error}</p>}
    {!data ? <p>Loading outreach…</p> : <>
      <div className="mod-summary" aria-label="Outreach summary">
        <div className={data.paused ? 'mod-alert' : ''}><strong>{data.paused ? 'Paused' : 'Running'}</strong><span>{data.paused ? data.pause_reason || 'No reason given' : 'Outreach status'}</span></div>
        <div><strong>{data.sent_today} of {data.daily_cap}</strong><span>Invitations today</span></div>
        <div><strong>{data.converted_total}</strong><span>Businesses that joined</span></div>
        <div><strong>{data.suppressed_total}</strong><span>Do not contact</span></div>
      </div>
      <PauseControl paused={data.paused} onDone={refresh} />
      <div className="segmented mod-tabs" role="tablist" aria-label="Outreach sections">
        {tabs.map(([key, label]) => <button key={key} role="tab" aria-selected={tab === key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>{label}</button>)}
      </div>
      {tab === 'jobs' && <Table empty="No jobs yet." head={['Posted', 'Job', 'Status', 'Invited', 'Considered', 'Note']}
        rows={data.queue.map((row) => [formatDateTime(row.created_at), row.job_id.slice(0, 8), labelFor(statusLabels, row.status), row.invited_count, row.candidate_count, row.note || ''])} />}
      {tab === 'invites' && <Table empty="No invitations yet." head={['Created', 'Business', 'Email', 'Status', 'Joined', 'Job']}
        rows={data.invites.map((row) => [formatDateTime(row.created_at), row.business_name, row.email, labelFor(statusLabels, row.status) + (row.error ? ` (${row.error})` : ''), row.converted_at ? formatDateTime(row.converted_at) : '', row.job_id.slice(0, 8)])} />}
      {tab === 'candidates' && <Table empty="No candidates logged yet." head={['When', 'Rank', 'Business', 'Website', 'Score', 'Decision']}
        rows={data.candidates.map((row) => [formatDateTime(row.created_at), row.rank ?? '', row.business_name || 'Cleared after 30 days', row.website_domain || '', row.score ?? '', labelFor(reasonLabels, row.reason)])} />}
      {tab === 'changes' && <Table empty="Outreach has never been paused." head={['When', 'Change', 'Reason']}
        rows={data.changes.map((row) => [formatDateTime(row.created_at), row.paused ? 'Paused' : 'Resumed', row.reason || ''])} />}
    </>}
  </div>;
}

function PauseControl({ paused, onDone }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (event) => {
    event.preventDefault();
    const next = !paused;
    const problem = validatePauseReason(next, reason);
    if (problem) { setError(problem); return; }
    setBusy(true); setError('');
    try { await setOutreachPaused(supabase, next, reason.trim()); setReason(''); onDone(); }
    catch (cause) { setError(cause.message); }
    finally { setBusy(false); }
  };
  return <form className="mod-form" onSubmit={submit} style={{ marginBottom: 16 }}>
    <label htmlFor="outreach-reason">{paused ? 'Note (optional)' : 'Why are you pausing outreach?'}</label>
    <input id="outreach-reason" value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} />
    {error && <p className="mod-error" role="alert">{error}</p>}
    <div className="mod-action-row"><button className="button button-blue" disabled={busy}>{busy ? 'Saving…' : paused ? 'Resume outreach' : 'Pause all outreach'}</button></div>
  </form>;
}

function Table({ head, rows, empty }) {
  if (!rows.length) return <p className="account-empty">{empty}</p>;
  return <div className="mod-table-wrap">
    <table className="mod-audit">
      <thead><tr>{head.map((label) => <th key={label} scope="col">{label}</th>)}</tr></thead>
      <tbody>{rows.map((cells, index) => <tr key={index}>{cells.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody>
    </table>
  </div>;
}
